/**
 * Verification Session List Component
 *
 * Displays a list of design verification sessions with search and actions.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { VerificationSession } from '../../types/verification';
import VerificationSessionListSkeleton from './session-list/VerificationSessionListSkeleton';
import VerificationSessionRow from './session-list/VerificationSessionRow';

interface VerificationSessionListProps {
  sessions: VerificationSession[];
  selectedSessionId: string | null;
  isLoading: boolean;
  error: string | null;
  onSessionSelect: (session: VerificationSession) => void;
  onSessionDelete?: (sessionId: string) => void;
  onSessionExport?: (sessionId: string) => void;
  onRefresh?: () => void;
  onCreate?: () => void;
}

const VerificationSessionList: React.FC<VerificationSessionListProps> = ({
  sessions,
  selectedSessionId,
  isLoading,
  error,
  onSessionSelect,
  onSessionDelete,
  onSessionExport,
  onRefresh,
  onCreate,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSessions = sessions.filter((session) => {
    const query = searchQuery.toLowerCase();
    return (
      session.title.toLowerCase().includes(query) ||
      (session.modelName && session.modelName.toLowerCase().includes(query))
    );
  });

  if (isLoading && sessions.length === 0) {
    return <VerificationSessionListSkeleton />;
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="revy-cjk text-lg font-semibold leading-[1.5]">
            {t('verification.sessions.title')}
          </h2>
          <div className="flex gap-0.5">
            {onRefresh && (
              <TooltipIconButton
                label={t('common.refresh')}
                icon={<RefreshCw strokeWidth={1.8} />}
                size="icon-sm"
                onClick={onRefresh}
              />
            )}
            {onCreate && (
              // `common.add` rather than `sessions.create`: the empty state
              // already owns that name, and two controls answering to one
              // accessible name is ambiguous for assistive tech.
              <TooltipIconButton
                label={t('common.add')}
                icon={<Plus strokeWidth={1.8} />}
                size="icon-sm"
                className="text-primary"
                onClick={onCreate}
              />
            )}
          </div>
        </div>

        <div className="relative mb-4">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="revy-cjk leading-[1.5]">{error}</AlertDescription>
          </Alert>
        )}

        {!isLoading && filteredSessions.length === 0 && (
          <div className="py-8 text-center">
            <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
              {sessions.length === 0
                ? t('verification.sessions.noSessions')
                : t('common.noResults')}
            </p>
            {sessions.length === 0 && onCreate && (
              <Button variant="outline" onClick={onCreate} className="mt-4">
                <Plus strokeWidth={1.8} />
                {t('verification.sessions.create')}
              </Button>
            )}
          </div>
        )}

        {/* The divider is a border on the rows rather than a separator element,
            so the <ul> keeps only <li> children. */}
        <ul className="[&>li+li]:border-t [&>li+li]:border-border">
          {filteredSessions.map((session) => (
            <VerificationSessionRow
              key={session.id}
              session={session}
              selected={session.id === selectedSessionId}
              onSessionSelect={onSessionSelect}
              onSessionDelete={onSessionDelete}
              onSessionExport={onSessionExport}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default VerificationSessionList;
